import { useEffect, useState } from 'react';
import { AgendamentoPagamentosService } from '../../../agendamentoPagamentos/Service';
import { useAppContext } from '../../../../contexts/AppContext';
import { UserService } from '../../usuario/Service';
import { normalizeSearch } from '../../../recorrencias/helpers';
import { SettingsRow, SettingsSection } from './SettingsGroup';
import { Check, Search } from 'lucide-react';

const service = new AgendamentoPagamentosService();
const userService = new UserService();

const weekDays = [
    { value: 0, label: 'Dom', name: 'Domingo' },
    { value: 1, label: 'Seg', name: 'Segunda-feira' },
    { value: 2, label: 'Ter', name: 'Terça-feira' },
    { value: 3, label: 'Qua', name: 'Quarta-feira' },
    { value: 4, label: 'Qui', name: 'Quinta-feira' },
    { value: 5, label: 'Sex', name: 'Sexta-feira' },
    { value: 6, label: 'Sáb', name: 'Sábado' },
];

type PayerMode = 'ALL_CLIENTS' | 'SELECTED_CLIENTS' | 'EXCLUDED_CLIENTS';

const normalizePayerMode = (value: unknown): PayerMode => {
    if (value === 'SELECTED_CLIENTS' || value === 'EXCLUDED_CLIENTS') return value;
    return 'ALL_CLIENTS';
};

export const AppointmentPaymentRules = () => {
    const { successSnackbar, errorSnackbar } = useAppContext();
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [loadingClients, setLoadingClients] = useState(false);
    const [enabled, setEnabled] = useState(false);
    const [requiredWeekDays, setRequiredWeekDays] = useState<number[]>([]);
    const [payerMode, setPayerMode] = useState<PayerMode>('ALL_CLIENTS');
    const [payerClientIds, setPayerClientIds] = useState<string[]>([]);
    const [clients, setClients] = useState<any[]>([]);
    const [clientSearch, setClientSearch] = useState('');
    const [pixExpirationMinutes, setPixExpirationMinutes] = useState<number | ''>(15);

    useEffect(() => {
        const load = async () => {
            try {
                setLoading(true);
                const { data: settings } = await service.getSettings();
                setEnabled(Boolean(settings?.enabled));
                setRequiredWeekDays(Array.isArray(settings?.requiredWeekDays) ? settings.requiredWeekDays : []);
                setPayerMode(normalizePayerMode(settings?.payerMode));
                setPayerClientIds(Array.isArray(settings?.payerClientIds) ? settings.payerClientIds : []);
                setPixExpirationMinutes(Number(settings?.pixExpirationMinutes || 15));
            } catch (error) {
                errorSnackbar('Não foi possível carregar a configuração de pagamentos.');
            } finally {
                setLoading(false);
            }
        };
        void load();
    }, []);

    useEffect(() => {
        const loadClients = async () => {
            try {
                setLoadingClients(true);
                const { data } = await userService.getClients();
                setClients(Array.isArray(data) ? data : []);
            } catch (error) {
                setClients([]);
            } finally {
                setLoadingClients(false);
            }
        };
        void loadClients();
    }, []);

    const toggleWeekDay = (day: number) => {
        setRequiredWeekDays((current) =>
            current.includes(day)
                ? current.filter((item) => item !== day)
                : [...current, day].sort((a, b) => a - b),
        );
    };

    const toggleClient = (clientId: string) => {
        setPayerClientIds((current) =>
            current.includes(clientId)
                ? current.filter((item) => item !== clientId)
                : [...current, clientId],
        );
    };

    const filteredClients = clients
        .filter((client) => {
            const term = normalizeSearch(clientSearch);
            if (!term) return true;
            return [client?.displayName, client?.email, client?.phoneNumber]
                .filter(Boolean)
                .some((value) => normalizeSearch(String(value)).includes(term));
        })
        .sort((left, right) => {
            const leftSelected = payerClientIds.includes(left?.uid);
            const rightSelected = payerClientIds.includes(right?.uid);
            return Number(rightSelected) - Number(leftSelected);
        });

    const handleSave = async () => {
        const minutes = Number(pixExpirationMinutes);

        if (enabled && (!Number.isFinite(minutes) || minutes < 1 || minutes > 1440)) {
            errorSnackbar('Informe uma validade do Pix entre 1 e 1440 minutos.');
            return;
        }

        if (enabled && !requiredWeekDays.length) {
            errorSnackbar('Selecione ao menos um dia para exigir o Pix.');
            return;
        }

        if (enabled && payerMode === 'SELECTED_CLIENTS' && !payerClientIds.length) {
            errorSnackbar('Selecione ao menos um cliente que deve pagar.');
            return;
        }

        try {
            setSaving(true);
            const { data } = await service.updateSettings({
                enabled,
                requiredWeekDays,
                payerMode,
                payerClientIds: payerMode === 'ALL_CLIENTS' ? [] : payerClientIds,
                pixExpirationMinutes: minutes || 15,
            });
            setPayerMode(normalizePayerMode(data?.payerMode));
            setPayerClientIds(Array.isArray(data?.payerClientIds) ? data.payerClientIds : []);
            successSnackbar('Configuração de pagamentos salva.');
        } catch (error) {
            errorSnackbar('Não foi possível salvar a configuração de pagamentos.');
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return (
            <div className="space-y-3" aria-busy="true">
                <div className="sf-skeleton h-16" />
                <div className="sf-skeleton h-24" />
            </div>
        );
    }

    const usesClientSelection = payerMode !== 'ALL_CLIENTS';
    const payerOptions: { value: PayerMode; label: string }[] = [
        { value: 'ALL_CLIENTS', label: 'Todos os clientes' },
        { value: 'SELECTED_CLIENTS', label: 'Somente clientes selecionados' },
        { value: 'EXCLUDED_CLIENTS', label: 'Todos, exceto clientes selecionados' },
    ];

    return (
        <div className="estab-settings space-y-6">
            <SettingsSection
                title="Pix nos autoagendamentos"
                footer="O horário fica reservado até o vencimento do Pix. Agendamentos feitos pela equipe não exigem pagamento."
            >
                <SettingsRow label="Exigir Pix" htmlFor="pix-enabled" inline>
                    <input
                        id="pix-enabled"
                        type="checkbox"
                        className="toggle toggle-success"
                        checked={enabled}
                        onChange={(event) => setEnabled(event.target.checked)}
                    />
                </SettingsRow>
                {enabled && (
                    <SettingsRow label="Validade (minutos)" htmlFor="pix-expiration">
                        <input
                            id="pix-expiration"
                            type="number"
                            min={1}
                            max={1440}
                            className="input estab-field"
                            value={pixExpirationMinutes}
                            onChange={(event) =>
                                setPixExpirationMinutes(event.target.value === '' ? '' : Number(event.target.value))
                            }
                        />
                    </SettingsRow>
                )}
            </SettingsSection>

            {enabled && (
                <>
                    <SettingsSection title="Dias que exigem pagamento">
                        <div className="sf-days p-3" role="group" aria-label="Dias que exigem pagamento">
                            {weekDays.map((day) => (
                                <button
                                    key={day.value}
                                    type="button"
                                    className="sf-day"
                                    aria-pressed={requiredWeekDays.includes(day.value)}
                                    aria-label={day.name}
                                    onClick={() => toggleWeekDay(day.value)}
                                >
                                    {day.label}
                                </button>
                            ))}
                        </div>
                    </SettingsSection>

                    <section>
                        <h2 className="sf-group-title">Quem deve pagar nesses dias</h2>
                        <ul className="sf-group">
                            {payerOptions.map((option) => (
                                <li key={option.value}>
                                    <button
                                        type="button"
                                        className="sf-row !min-h-[2.75rem]"
                                        aria-pressed={payerMode === option.value}
                                        onClick={() => setPayerMode(option.value)}
                                    >
                                        <span className="sf-row-body text-sm text-base-content">{option.label}</span>
                                        {payerMode === option.value && (
                                            <Check className="sf-row-check" aria-hidden />
                                        )}
                                    </button>
                                </li>
                            ))}
                        </ul>
                    </section>

                    {usesClientSelection && (
                        <section>
                            <h2 className="sf-group-title">
                                {payerMode === 'EXCLUDED_CLIENTS' ? 'Clientes dispensados do Pix' : 'Clientes que devem pagar'}
                                {' · '}
                                {payerClientIds.length} {payerClientIds.length === 1 ? 'selecionado' : 'selecionados'}
                            </h2>
                            <label className="sf-search mb-2">
                                <Search aria-hidden />
                                <input
                                    type="search"
                                    value={clientSearch}
                                    onChange={(event) => setClientSearch(event.target.value)}
                                    placeholder="Buscar cliente"
                                    aria-label="Buscar cliente"
                                />
                            </label>

                            {loadingClients ? (
                                <div className="sf-skeleton h-32" aria-busy="true" />
                            ) : !filteredClients.length ? (
                                <div className="sf-state !py-6">
                                    <h2>Nenhum cliente encontrado</h2>
                                </div>
                            ) : (
                                <ul className="sf-group max-h-80 overflow-y-auto overscroll-contain">
                                    {filteredClients.map((client) => {
                                        const selected = payerClientIds.includes(client.uid);

                                        return (
                                            <li key={client.uid}>
                                                <button
                                                    type="button"
                                                    className="sf-row"
                                                    aria-pressed={selected}
                                                    onClick={() => toggleClient(client.uid)}
                                                >
                                                    <span className="sf-row-body">
                                                        <span className="sf-row-title !font-medium">
                                                            {client.displayName || client.phoneNumber || client.email || 'Cliente'}
                                                        </span>
                                                        {(client.phoneNumber || client.email) && (
                                                            <span className="sf-row-sub">
                                                                {[client.phoneNumber, client.email].filter(Boolean).join(' · ')}
                                                            </span>
                                                        )}
                                                    </span>
                                                    {selected && <Check className="sf-row-check" aria-hidden />}
                                                </button>
                                            </li>
                                        );
                                    })}
                                </ul>
                            )}
                        </section>
                    )}
                </>
            )}

            <div className="estab-savebar">
                <button
                    type="button"
                    className="btn btn-primary w-full sm:w-auto sm:min-w-[11rem]"
                    disabled={saving}
                    onClick={handleSave}
                >
                    {saving && <span className="loading loading-spinner loading-sm"></span>}
                    Salvar alterações
                </button>
            </div>
        </div>
    );
};

export const AsaasPaymentsIntegration = () => {
    const { successSnackbar, errorSnackbar } = useAppContext();
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [environment, setEnvironment] = useState<'sandbox' | 'production'>('sandbox');
    const [apiKey, setApiKey] = useState('');
    const [apiKeyLast4, setApiKeyLast4] = useState('');
    const [webhookUrl, setWebhookUrl] = useState('');
    const [webhookAuthToken, setWebhookAuthToken] = useState('');
    const [savedWebhookAuthToken, setSavedWebhookAuthToken] = useState('');
    const [webhookConfigured, setWebhookConfigured] = useState(false);
    const [lastError, setLastError] = useState('');

    const applySettings = (data: any) => {
        setEnvironment(data?.environment === 'production' ? 'production' : 'sandbox');
        setApiKeyLast4(data?.apiKeyLast4 || '');
        setWebhookUrl(data?.webhookUrl || '');
        setWebhookAuthToken(data?.webhookAuthToken || '');
        setSavedWebhookAuthToken(data?.webhookAuthToken || '');
        setWebhookConfigured(Boolean(data?.webhookConfigured));
        setLastError(data?.lastError || '');
    };

    useEffect(() => {
        const load = async () => {
            try {
                setLoading(true);
                const { data } = await service.getSettings();
                applySettings(data);
            } catch (error) {
                errorSnackbar('Não foi possível carregar a integração Asaas.');
            } finally {
                setLoading(false);
            }
        };
        void load();
    }, []);

    const handleSave = async () => {
        const token = webhookAuthToken.trim();
        const tokenChanged = token !== savedWebhookAuthToken;
        if (tokenChanged && (token.length < 32 || token.length > 255 || /\s/.test(token))) {
            errorSnackbar('O token do webhook deve ter entre 32 e 255 caracteres, sem espaços.');
            return;
        }

        try {
            setSaving(true);
            const { data } = await service.updateSettings({
                environment,
                apiKey: apiKey.trim() || undefined,
                webhookAuthToken: tokenChanged ? token : undefined,
            });
            setApiKey('');
            applySettings(data);
            successSnackbar('Integração Asaas salva.');
        } catch (error: any) {
            const message = error?.response?.data?.message;
            errorSnackbar(typeof message === 'string' ? message : 'Não foi possível salvar a integração Asaas.');
        } finally {
            setSaving(false);
        }
    };

    if (loading) return <span className="loading loading-spinner loading-sm"></span>;

    return (
        <div className="space-y-4">
            <label className="form-control">
                <span className="label-text mb-1">Ambiente Asaas</span>
                <select
                    className="select select-bordered"
                    value={environment}
                    onChange={(event) => setEnvironment(event.target.value as 'sandbox' | 'production')}
                >
                    <option value="sandbox">Sandbox</option>
                    <option value="production">Produção</option>
                </select>
            </label>

            <label className="form-control">
                <span className="label-text mb-1">API key Asaas</span>
                <input
                    type="password"
                    autoComplete="new-password"
                    className="input input-bordered"
                    value={apiKey}
                    onChange={(event) => setApiKey(event.target.value)}
                    placeholder={apiKeyLast4 ? `Configurada terminando em ${apiKeyLast4}` : 'Cole a API key do Asaas'}
                />
                <span className="mt-1 text-xs text-base-content/60">
                    A chave salva não é exibida. Preencha somente para cadastrar ou substituir.
                </span>
            </label>

            {(webhookUrl || webhookAuthToken) && (
                <div className="rounded-xl border border-base-200 bg-base-200/40 p-3 text-xs">
                    <div className="font-semibold">
                        Webhook {webhookConfigured ? 'configurado' : 'para configurar no Asaas'}
                    </div>
                    {webhookUrl && <div className="mt-1 break-all">URL: {webhookUrl}</div>}
                </div>
            )}

            <label className="form-control">
                <span className="label-text mb-1">Token de autenticação do webhook</span>
                <input
                    type="text"
                    autoComplete="off"
                    spellCheck={false}
                    className="input input-bordered"
                    value={webhookAuthToken}
                    onChange={(event) => setWebhookAuthToken(event.target.value)}
                    maxLength={255}
                    placeholder="Informe o token do webhook Asaas"
                    disabled={saving}
                />
                <span className="mt-1 text-xs text-base-content/60">
                    Use entre 32 e 255 caracteres, sem espaços. Se o webhook foi configurado manualmente,
                    informe o mesmo token cadastrado no Asaas. Sem preencher no primeiro cadastro, um token será gerado.
                </span>
            </label>

            {lastError && (
                <div className="rounded-xl border border-warning/30 bg-warning/10 p-3 text-xs text-warning">
                    {lastError}
                </div>
            )}

            <div className="flex justify-end">
                <button type="button" className="btn btn-primary" disabled={saving} onClick={handleSave}>
                    {saving ? <span className="loading loading-spinner loading-sm"></span> : null}
                    Salvar integração
                </button>
            </div>
        </div>
    );
};
